import {useFormik} from 'formik'
import {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, useSearchParams} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import * as Yup from 'yup'
import {PasswordMeterComponent} from '../../../../_gori/assets/ts/components'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {Button, ValidationErrorModal} from '../../../../_gori/partials/widgets'
import {InputPassword} from '../../../../_gori/partials/widgets/form/InputPassword'
import AuthService from '../core/_requests'
import {ROUTES} from '../../../../_gori/constants/_routes'
import {useAuth} from '../core/Auth'

export function NewPassword() {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const [searchParams] = useSearchParams()
  const {authYup} = UseYupValidate()
  const token = searchParams.get('token')
  const [loading, setLoading] = useState<{first: boolean; after: boolean}>({
    first: true,
    after: false,
  })
  const [expired, setExpired] = useState<boolean>(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [validationErrors, setValidationErrors] = useState<any>()
  const [accountTradeShow, setAccountTradeShow] = useState<boolean>(false)
  const {saveAuth, handleSaveCurrentUser} = useAuth()

  const newPasswordSchema = Yup.object().shape({
    password: authYup.password(),
    password_confirmation: authYup.passwordConfirmation(),
  })

  const initialValues = {
    password: '',
    password_confirmation: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: newPasswordSchema,
    onSubmit: async (values) => {
      setLoading((prev) => ({...prev, after: true}))

      const config = {cancelToken: newCancelToken()}
      try {
        if (!token) return

        if (accountTradeShow) {
          const tradeShow = await AuthService.tradeShowNewPassword(
            {
              ...values,
              token: token,
            },
            config
          )

          saveAuth(tradeShow?.token)
          handleSaveCurrentUser(tradeShow?.user)
        } else {
          await AuthService.newPassword(
            {
              ...values,
              token: token,
            },
            config
          )

          setHasErrors(false)
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, after: false}))
      }
    },
  })

  const checkUserVerified = useCallback(async () => {
    if (!token) {
      setLoading((prev) => ({...prev, first: false}))
      return setHasErrors(true)
    }
    const config = {
      params: {
        confirmation_token: token,
        type: 'reset-password',
      },
    }

    try {
      setLoading((prev) => ({...prev, first: true}))
      const response = await AuthService.verifyToken(config)
      setAccountTradeShow(!!response.trade_show_account)
    } catch (error: any) {
      if (error?.response?.data?.error?.fields?.expired) {
        setExpired(true)
      }
      setHasErrors(true)
    } finally {
      setLoading((prev) => ({...prev, first: false}))
      PasswordMeterComponent.bootstrap()
    }
  }, [token])

  useEffect(() => {
    checkUserVerified()
  }, [checkUserVerified])

  return (
    <>
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='w-lg-550px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          {validationErrors && (
            <ValidationErrorModal
              handleClose={() => {
                setValidationErrors(undefined)
              }}
              response={validationErrors}
            />
          )}
          {loading.first ? (
            <div>
              <div className='text-center'>
                <span className='fs-1 col-6 placeholder placeholder-lg rounded-2 bg-secondary' />
                <span className='fs-1 col-10 mt-3 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
              <span className='btn col-12 mt-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              <span className='btn col-12 mt-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              <div className='d-flex justify-content-center'>
                <span className='btn col-4 mt-6 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
            </div>
          ) : (
            <div className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
              {token && hasErrors === undefined && (
                <>
                  <div className='mb-10 text-center'>
                    <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'NEW_PASSWORD'})}</h1>
                    <div className='text-gray-400 fw-bold fs-4'>
                      {intl.formatMessage({id: 'ALREADY_HAVE_RESET_YOUR_PASSWORD'})}{' '}
                      <Link
                        to={ROUTES.AUTH.LOGIN}
                        className='text-primary-deep text-hover-primary fw-bolder'
                      >
                        {intl.formatMessage({id: 'SIGN_IN_HERE'})}
                      </Link>
                    </div>
                  </div>
                  <InputPassword
                    className={'mb-10'}
                    label={intl.formatMessage({id: 'PASSWORD'})}
                    size={'lg'}
                    strengthMessage={intl.formatMessage({id: 'PASSWORD_STRENGTH_MESSAGE'})}
                    formik={formik}
                    name={'password'}
                    required
                  />
                  <InputPassword
                    className={'mb-10'}
                    label={intl.formatMessage({id: 'CONFIRM_PASSWORD'})}
                    size='lg'
                    openHighlight={false}
                    formik={formik}
                    name={'password_confirmation'}
                    required
                  />
                  <div className='text-center'>
                    <Button
                      className='btn btn-lg btn-advanced-primary fw-bolder'
                      label={intl.formatMessage({id: 'SUBMIT'})}
                      loadingText={intl.formatMessage({id: 'SUBMIT'})}
                      loading={loading.after}
                      disabled={loading.after || !formik.isValid}
                      event={formik.submitForm}
                    />
                  </div>
                </>
              )}

              {hasErrors === false && (
                <>
                  <div className='mb-10 text-center'>
                    <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'NEW_PASSWORD'})}</h1>
                  </div>

                  <div className='mb-lg-10 alert bg-light-primary alert-dismissible'>
                    <div className='alert-text'>
                      {intl.formatMessage({
                        id: 'YOU_HAVE_SUCCESSFULLY_RESET_YOUR_PASSWORD',
                      })}
                    </div>
                  </div>

                  <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                    <Link to={ROUTES.AUTH.LOGIN} className='link-primary fw-bolder'>
                      <button
                        type='submit'
                        id='gori_sign_in_submit'
                        className='btn btn-lg btn-advanced-primary fw-bolder me-4'
                      >
                        {intl.formatMessage({id: 'LOGIN'})}
                      </button>
                    </Link>
                  </div>
                </>
              )}

              {(!token || hasErrors === true) && (
                <>
                  <div className='mb-10 text-center'>
                    <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'NEW_PASSWORD'})}</h1>
                  </div>

                  <div className='mb-10 alert alert-danger alert-dismissible'>
                    <div className='alert-text'>
                      {expired
                        ? intl.formatMessage({id: 'INVALID_CONFIRMATION_TOKEN_EXPIRED'})
                        : intl.formatMessage({id: 'INVALID_CONFIRMATION_TOKEN'})}
                    </div>
                  </div>

                  <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                    <Link to={ROUTES.AUTH.LOGIN} className='link-primary fw-bolder'>
                      <button
                        type='submit'
                        id='gori_sign_in_submit'
                        className='btn btn-lg btn-advanced-primary fw-bolder me-4'
                      >
                        {intl.formatMessage({id: 'LOGIN'})}
                      </button>
                    </Link>
                    <Link to={ROUTES.AUTH.FORGOT_PASSWORD} className='link-primary fw-bolder'>
                      <button
                        type='submit'
                        id='gori_sign_in_submit'
                        className='btn btn-lg btn-advanced-secondary fw-bolder'
                      >
                        {intl.formatMessage({id: 'RESET_PASSWORD'})}
                      </button>
                    </Link>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </CSSTransition>
    </>
  )
}
